/**
 * Setup → "History" for a check's temperature range, from its definition
 * revisions. Only versions whose range differs from the one before are
 * listed; renames, schedule edits etc. are left out.
 */

import moment from 'moment'
import {
  FOOD_SAFETY_DEFINITION_REVISION_ACTION,
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_SECTION,
} from 'src/constants/foodSafety'
import { formatFoodSafetyTempRange } from 'src/utils/foodSafetyTempRange'

/** Sections whose checks carry their own range. */
export const FOOD_SAFETY_RANGE_HISTORY_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  FOOD_SAFETY_SECTION.COOKING_REHEATING,
  FOOD_SAFETY_SECTION.COOLING,
  FOOD_SAFETY_SECTION.DELIVERIES,
])

export function hasFoodSafetyRangeHistory(section) {
  return FOOD_SAFETY_RANGE_HISTORY_SECTIONS.includes(section)
}

const toNumberOrNull = value =>
  value == null || value === '' || Number.isNaN(Number(value))
    ? null
    : Number(value)

/** "0–5°C", "75°C or above", "8°C or below", or what applies without one. */
export function formatFoodSafetyRevisionRange(snapshot, section) {
  const config = snapshot?.config || {}
  if (
    section === FOOD_SAFETY_SECTION.DELIVERIES &&
    config.deliveryTempBand === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT
  ) {
    return 'No temperature range'
  }
  const min = toNumberOrNull(config.tempMin)
  const max = toNumberOrNull(config.tempMax)
  if (min != null && max != null) return formatFoodSafetyTempRange(min, max)
  if (min != null) return `${min}°C or above`
  if (max != null) return `${max}°C or below`
  return section === FOOD_SAFETY_SECTION.COOLING
    ? 'Venue cooling target'
    : 'No range'
}

const formatChangedAt = at => {
  const m = moment(at)
  return m.isValid() ? m.format('ddd D MMM YYYY · HH:mm') : ''
}

const formatLocalDay = localDate => {
  const m = moment(localDate, 'YYYY-MM-DD', true)
  return m.isValid() ? m.format('ddd D MMM') : ''
}

/**
 * Newest first: `{ key, text, meta }` per range the check has had.
 * @param {object[]} revisions Express definition revisions (any order)
 */
export function listFoodSafetyRangeHistory(revisions, section) {
  const ordered = [...(revisions || [])]
    .filter(rev => rev && rev.snapshot)
    .sort((a, b) => Number(a.revision) - Number(b.revision))

  const entries = []
  let previous = null
  for (const rev of ordered) {
    const range = formatFoodSafetyRevisionRange(rev.snapshot, section)
    if (previous != null && range === previous) continue
    const by = String(rev.changedByName || '').trim()
    const when = formatChangedAt(rev.changedAt)
    let text
    let from = ''
    if (previous == null) {
      text =
        rev.action === FOOD_SAFETY_DEFINITION_REVISION_ACTION.BASELINE
          ? `${range} when history began`
          : `Set up with ${range}`
    } else {
      text = `${previous} → ${range}`
      const day = formatLocalDay(rev.effectiveLocalDate)
      from = day ? `readings from ${day}` : ''
    }
    entries.push({
      key: String(rev._id || rev.revision),
      text,
      meta: [when, by, from].filter(Boolean).join(' · '),
    })
    previous = range
  }
  return entries.reverse()
}
